import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { Bookmark } from '../../models/bookmark';
import { withBookmarkSelectors } from './bookmark.selectors';
import { BookmarkService } from '../../services/bookmark/bookmark.service';
import { inject } from '@angular/core';
import { ActiveUserStore } from '../user/active-user.state';
import { withLogging } from '../features/logger.feature';
import { loadWhen } from '../features/load-when';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';

export type BookmarkState = BaseState<Bookmark>;

export const initialState: BookmarkState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityType: 'Bookmark',
  entityLabel: 'Ansichten',
  filter: {
    query: '',
    category: [],
    tags: [],
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['bookmarkAdded', 'runBookmarkValidation'],
    update: ['bookmarkUpdated', 'runBookmarkValidation'],
  },
};

export const BookmarkStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<Bookmark>(BookmarkService),
  withLogging(initialState.entityType),
  withBookmarkSelectors(),
  withMethods(
    (
      store,
      bookmarkService = inject(BookmarkService),
      confirmationDialogService = inject(ConfirmationDialogService)
    ) => ({
      /**
       * fill pool state with pool-indicators
       */
      async findByCategoryIds(categoryIds: string[]) {
        patchState(store, { loading: true });

        try {
          const items = await bookmarkService.getByCategoryIds(categoryIds);
          patchState(store, {
            items: items,
            loading: false,
            initialized: true,
            itemsLoaded: true,
          });
        } catch (error) {
          patchState(store, { loading: false, initialized: true, error });
          confirmationDialogService.addErrorMessage(
            `${store.entityLabel()} konnten nicht geladen werden.`,
            'Daten konnten nicht geladen werden'
          );
        }
      },
    })
  ),
  withHooks({
    onInit(store, activeUserStore = inject(ActiveUserStore)) {
      // This store can be constructed before login: the always-mounted
      // ToolOverlayComponent injects RoutingService -> TopicHelperService,
      // which field-injects this store directly. At that point
      // canSeeAllInWorkspaceType() is a one-shot check against a `null`
      // user and always evaluates false - and since InitializationHandler
      // later trusts this store's own onInit to have handled loading for
      // admins in dataBackend context (see handleBookmarks()), nothing
      // ever retries. Watch reactively instead, so it fires once real
      // permission data is available (or changes) rather than deciding
      // once, permanently, off of incomplete data.
      loadWhen(
        () =>
          activeUserStore.canSeeAllInWorkspaceType('bookmarkCategory') &&
          activeUserStore.context() === 'dataBackend' &&
          activeUserStore.commune(),
        () => store.loadAllItemsByPromise()
      );
    },
  })
);
